import { Container } from '~/components/container';
import { HStack, Stack, VStack } from '~/components/stack';
import { LandingPurchaseButton } from '~/routes/_index/landing-purchase-button';
import { cn } from '~/utils/cn';
import { LandingSection } from '~/routes/_index/landing-section';
import { LandingSectionHeader } from '~/routes/_index/landing-section-header';
import { getExamName } from '~/routes/_index/landing-texts';
import { env } from '~/config/env';
import { useActivePromo } from '~/features/promo/use-active-promo';
import { ROUTES } from '~/utils/routes';

export const PRICES = {
  usmle: {
    monthly: {
      normal: 49.99,
      promo: 24.99,
    },
    annual: {
      normal: 39.99,
      promo: 19.99,
    },
  },
  ems: {
    monthly: {
      normal: 29.99,
      promo: 14.99,
    },
    annual: {
      normal: 19.99,
      promo: 9.99,
    },
  },
  msp: {
    monthly: {
      normal: 19.99,
      promo: 9.99,
    },
    annual: {
      normal: 15.99,
      promo: 7.99,
    },
    oneTimePayment: {
      normal: 5.49,
    },
  },
};

export function LandingPricingSection() {
  return (
    <LandingSection
      id={ROUTES.LANDING.PRICING.replace('/#', '')}
      className="bg-card"
    >
      <Container>
        <VStack className="items-center gap-8">
          <VStack className="text-center gap-4 items-center">
            <LandingSectionHeader>
              Choose Your Perfect {getExamName()} Prep Plan
            </LandingSectionHeader>
            <p>
              Flexible plans tailored to your study pace - whether{' '}
              <b>intensive or long-term</b>, we’ve got you covered!
            </p>
          </VStack>
          <Stack className={cn('gap-4 flex-col-reverse', 'lg:flex-row')}>
            <PricingCard
              title="Monthly Access"
              price={PRICES[env.VITE_APP].monthly}
              period="monthly"
            />
            <PricingCard
              variant="deal"
              title="Yearly Access"
              price={PRICES[env.VITE_APP].annual}
              period="yearly"
            />
          </Stack>
        </VStack>
      </Container>
    </LandingSection>
  );
}

function PricingCard({
  title,
  price,
  period,
  variant = 'default',
}: {
  title: string;
  price: { normal: number; promo: number };
  period: 'monthly' | 'yearly';
  variant?: 'default' | 'deal';
}) {
  const { promo } = useActivePromo();
  const normalPriceStr = price.normal.toFixed(2);
  const promoPriceStr = price.promo.toFixed(2);
  const currentPrice = promo ? price.promo : price.normal;
  const currentPriceStr = currentPrice.toFixed(2);

  return (
    <VStack
      className={cn('p-6 border rounded-md gap-4 relative', {
        'border-amber-400': variant === 'deal',
      })}
    >
      {variant === 'deal' && (
        <span className="absolute top-4 right-4 bg-green-200 text-green-950 text-sm font-bold p-2 rounded-md">
          Best Deal
        </span>
      )}
      <VStack className="gap-1">
        <span className="text-muted-foreground">{title}</span>
        <HStack
          className={cn('justify-between pt-1 pb-3', {
            hidden: promo === null,
          })}
        >
          <span className="text-muted-foreground line-through">
            <span className="text-lg">${normalPriceStr.split('.')[0]}.</span>
            <span className="text-sm align-top">
              {normalPriceStr.split('.')[1]}
            </span>
          </span>
          <span className="text-emerald-600">
            Save $
            {Math.round(price.normal - price.promo) *
              (period === 'yearly' ? 12 : 1)}
          </span>
        </HStack>
        <span className="font-bold">
          <span className="text-4xl">${currentPriceStr.split('.')[0]}</span>.
          <span className="align-top">{currentPriceStr.split('.')[1]}</span>
          /month
        </span>
        <span className="text-sm">(billed {period})</span>
      </VStack>
      <span className="text-4xl text-theme font-bold">Unlimited access</span>
      <LandingPurchaseButton
        period={period}
        variant={variant === 'default' ? 'outline' : 'premium'}
      />
    </VStack>
  );
}
